import React, { useState, useEffect, useCallback } from 'react';
import { usageAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

// ── compact progress bar ──────────────────────────────────────────────────────
const UsageBar = ({ used = 0, limit = -1 }) => {
  if (limit === -1) {
    return (
      <div className="flex items-center gap-2 flex-1">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full" />
        <span className="text-xs text-gray-400 whitespace-nowrap">{used.toLocaleString()} / ∞</span>
      </div>
    );
  }
  const pct    = Math.min(100, Math.round((used / limit) * 100));
  const isOver = used > limit;
  const barCol = isOver ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-400' : 'bg-indigo-500';
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barCol}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs whitespace-nowrap font-medium ${isOver ? 'text-red-600' : 'text-gray-500'}`}>
        {used.toLocaleString()} / {limit.toLocaleString()}
        {isOver && <span className="ml-1 text-red-600 font-bold">(+{(used - limit).toLocaleString()})</span>}
      </span>
    </div>
  );
};

const METRICS = [
  // { icon: '🤖', label: 'AI Queries',       usedKey: 'aiQueriesUsed',      limitKey: 'aiQueriesLimit',      resetType: 'ai',            adjustField: 'aiQueriesUsed'      },
  { icon: '👥', label: 'Customers',        usedKey: 'aiQueriesUsed',      limitKey: 'customersLimit',      resetType: 'ai',            adjustField: 'aiQueriesUsed'      },
  { icon: '🧾', label: 'AI Receipt Scans',    usedKey: 'receiptScansUsed',   limitKey: 'receiptScansLimit',   resetType: 'receipt',       adjustField: 'receiptScansUsed'   },
  { icon: '📄', label: 'Manual Invoices',  usedKey: 'manualInvoicesUsed', limitKey: 'manualInvoicesLimit', resetType: 'manualInvoice', adjustField: 'manualInvoicesUsed' },
  // { icon: '⚡', label: 'API Tokens',       usedKey: 'apiTokensUsed',      limitKey: 'apiTokensLimit',      resetType: 'apiTokens',     adjustField: 'apiTokensUsed'      },
  { icon: '📦', label: 'Items',            usedKey: 'apiTokensUsed',      limitKey: 'itemsLimit',          resetType: 'apiTokens',     adjustField: 'apiTokensUsed'      }
];

const CustomerUsagePanel = ({ customerId }) => {
  const [usage, setUsage]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [adjusting, setAdjusting] = useState(null); // which metric is being adjusted
  const [adjustVal, setAdjustVal] = useState('');

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usageAPI.getForCustomer(customerId);
      setUsage(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const handleReset = async (resetType, label) => {
    if (!window.confirm(`Reset ${label} usage for this period? This will NOT unblock the customer if other limits are still exceeded.`)) return;
    try {
      await usageAPI.resetUsage(usage._id, resetType);
      toast.success(`${label} usage reset`);
      fetchUsage();
    } catch (err) { toast.error(err.response?.data?.message || 'Reset failed'); }
  };

  const handleAdjust = async (metric) => {
    const val = parseInt(adjustVal);
    if (isNaN(val) || val < 0) return toast.error('Enter a valid number');
    try {
      await usageAPI.adjustUsage(usage._id, { field: metric.adjustField, value: val, mode: 'set' });
      toast.success('Usage updated');
      setAdjusting(null);
      setAdjustVal('');
      fetchUsage();
    } catch (err) { toast.error(err.response?.data?.message || 'Adjust failed'); }
  };

  if (loading) return <div className="p-6 flex justify-center"><Spinner /></div>;
  if (!usage)  return <p className="p-4 text-gray-400 text-sm">No usage data yet.</p>;

  const isBlocked = usage.blockedReasons?.length > 0;

  return (
    <div className="space-y-4">
      {/* Period header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-700">Current Billing Period</p>
          <p className="text-xs text-gray-400">{formatDate(usage.periodStart)} → {formatDate(usage.periodEnd)}</p>
        </div>
        {isBlocked && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <span className="text-red-500 text-sm">🚫</span>
            <div>
              <p className="text-xs font-bold text-red-700">Customer Blocked</p>
              <p className="text-xs text-red-500">Exceeded: {usage.blockedReasons.join(', ')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Metric rows */}
      <div className="space-y-2">
        {METRICS.map(metric => {
          const used  = usage[metric.usedKey]  ?? 0;
          const limit = usage[metric.limitKey] ?? -1;
          const over  = limit !== -1 && used > limit;
          const isAdj = adjusting === metric.adjustField;

          return (
            <div key={metric.usedKey} className={`rounded-xl border p-3 ${over ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <span className="text-lg w-6 text-center flex-shrink-0">{metric.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${over ? 'text-red-700' : 'text-gray-700'}`}>
                      {metric.label}
                      {over && <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-xs">OVER LIMIT</span>}
                    </span>
                  </div>
                  <UsageBar used={used} limit={limit} />
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {!isAdj ? (
                    <>
                      <button
                        onClick={() => { setAdjusting(metric.adjustField); setAdjustVal(String(used)); }}
                        className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-gray-100 transition text-gray-600"
                      >
                        Set
                      </button>
                      <button
                        onClick={() => handleReset(metric.resetType, metric.label)}
                        className="text-xs px-2 py-1 bg-white border border-gray-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition text-gray-500"
                      >
                        Reset
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="number" min="0" value={adjustVal}
                        onChange={e => setAdjustVal(e.target.value)}
                        className="w-20 px-2 py-1 text-xs border border-indigo-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        autoFocus
                      />
                      <button onClick={() => handleAdjust(metric)} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">✓</button>
                      <button onClick={() => setAdjusting(null)} className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300">✕</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reset all */}
      <div className="flex justify-end pt-1">
        <button
          onClick={() => handleReset('all', 'all usage')}
          className="text-xs text-gray-500 hover:text-red-600 hover:underline transition"
        >
          Reset all usage for this period
        </button>
      </div>
    </div>
  );
};

export default CustomerUsagePanel;
